
import { Link } from 'react-router-dom';
import { useMemo } from 'react';

type Service = {
  title: string;
  description: string;
  icon: string;
  link: string;
  image: string;
};

export default function Services() {
  /** Defensive data definition – guarantees every service has the required fields */
  const services: Service[] = useMemo(
    () => [
      {
        title: 'Residential Renovations',
        description:
          'Complete home transformations with expert construction and refined finishes',
        icon: 'ri-home-4-line',
        link: '/residential-renovations',
        image:
          'https://public.readdy.ai/ai/img_res/9838285bc58cd5cf92563e408dfbb553.jpg',
      },
      {
        title: 'Commercial Construction',
        description:
          'High-end fit-outs and renovations for offices, retail, and hospitality',
        icon: 'ri-building-2-line',
        link: '/commercial-construction',
        image:
          'https://readdy.ai/api/search-image?query=luxury%20commercial%20office%20interior%20modern%20workspace%20design%20contemporary%20reception%20area%20with%20marble%20desk%20gold%20fixtures%20professional%20lighting%20high%20end%20finishes%20sophisticated%20business%20environment%20premium%20construction%20quality%20elegant%20corporate%20space&width=600&height=800&seq=service-commercial-001&orientation=portrait',
      },
      {
        title: 'Structural Upgrades',
        description:
          'Expert structural and non‑structural alterations with full compliance',
        icon: 'ri-tools-line',
        link: '/#contact',
        image:
          'https://readdy.ai/api/search-image?query=structural%20renovation%20construction%20work%20showing%20modern%20architectural%20framework%20steel%20beams%20concrete%20work%20professional%20building%20upgrade%20licensed%20contractor%20quality%20construction%20materials%20precision%20engineering%20safe%20compliant%20building%20practices&width=600&height=800&seq=service-structural-001&orientation=portrait',
      },
    ],
    []
  );

  /** Helper to safely render an image – falls back to a solid colour if the src fails to load */
  const ImageWithFallback = ({
    src,
    alt,
    className,
  }: {
    src: string;
    alt: string;
    className?: string;
  }) => {
    const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      // Replace broken image with a subtle placeholder colour
      e.currentTarget.src =
        'data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" width="600" height="800"><rect width="100%" height="100%" fill="%23e5e7eb"/></svg>';
    };

    return (
      <img
        src={src}
        alt={alt}
        className={className}
        onError={handleError}
        loading="lazy"
      />
    );
  };

  return (
    <section
      id="services"
      className="py-16 md:py-24 lg:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-20">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-[#D4AF37] mb-4">
            Our Services
          </h2>
          <div className="w-16 h-0.5 bg-[#D4AF37] mx-auto"></div>
        </div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, index) => (
            <div
              key={service.title ?? index}
              className="group relative rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
              style={{ aspectRatio: '3/4' }}
            >
              {/* Background Image */}
              <div className="absolute inset-0">
                <ImageWithFallback
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              </div>

              {/* Gold Border Accent */}
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#D4AF37] transition-all duration-500 group-hover:w-1.5"></div>

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 space-y-3 md:space-y-4">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i
                    className={`${service.icon} text-2xl md:text-3xl text-[#D4AF37]`}
                    aria-hidden="true"
                  ></i>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white">
                  {service.title}
                </h3>
                <p className="text-sm md:text-base text-white/90 leading-relaxed">
                  {service.description}
                </p>

                {/* Link handling – internal vs external/anchor */}
                {service.link.startsWith('/') && !service.link.startsWith('/#') ? (
                  <Link
                    to={service.link}
                    className="inline-flex items-center gap-2 text-[#D4AF37] text-sm font-semibold hover:gap-3 transition-all"
                  >
                    Learn More
                    <i className="ri-arrow-right-line" aria-hidden="true"></i>
                  </Link>
                ) : (
                  <a
                    href={service.link}
                    className="inline-flex items-center gap-2 text-[#D4AF37] text-sm font-semibold hover:gap-3 transition-all"
                  >
                    Learn More
                    <i className="ri-arrow-right-line" aria-hidden="true"></i>
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
